import React, { useMemo } from 'react';

import { Checkbox, Tooltip } from 'antd';
import {
  COPY_TOOL_TASK_NO_CATEGORISED_STEPS_REASON,
  COPY_TOOL_TASK_NO_MATCH_REASON,
  COPY_TOOL_TASK_NO_STEPS_REASON,
  COPY_TOOL_TASKS_EMPTY_MESSAGE,
  COPY_TOOL_TASKS_NO_SOURCE_MESSAGE,
} from 'constants/messageConstants';
import {
  COPY_TOOL_TASK_STEP_COUNT_LABEL,
  COPY_TOOL_TASKS_COUNT_LABEL,
  COPY_TOOL_TASKS_LABEL,
} from 'constants/uiConstants';
import { CopyMatchRule } from 'interfaces/ICopyPaddockData';
import { getInputTaskTypeLabel } from 'interfaces/IInputTask';
import { getTaskSteps } from 'pages/FarmsPage/Workspace/Tasks/stepCalculations';
import { formatDate } from 'pages/FarmsPage/Workspace/Tasks/taskFactory';
import MergeClassNames from 'utils/MergeClassNames';

import {
  CopyDataFilterContext,
  CopyDataFilterState,
  getStepsInCategories,
  isTaskCopyable,
} from './copyDataFilter';

interface CopyTaskChecklistProps {
  /** Null until a Source document is ticked. */
  filter: CopyDataFilterState | null;
  context: CopyDataFilterContext | null;
  isFullCopy: boolean;
  /** Source's Plan or Actual label, for the empty message. */
  modeLabel: string;
  onTaskCheck: (taskId: string, checked: boolean) => void;
}

/**
 * The Source's tasks for its mode, ticked as the final selection. A ticked
 * task the rules leave out stays ticked, with the reason, so it can come back.
 * Always shown: without a Source or tasks it says what to do instead.
 */
const CopyTaskChecklist: React.FC<CopyTaskChecklistProps> = ({
  filter,
  context,
  isFullCopy,
  modeLabel,
  onTaskCheck,
}) => {
  const rows = useMemo(() => {
    if (!filter || !context) return [];
    const categories = new Set(filter.categories);
    const ticked = new Set(filter.taskIds);
    const isExact = !isFullCopy && filter.matchRule === CopyMatchRule.Exact;
    return context.tasks.map((task) => {
      const steps = getTaskSteps(task);
      const isCopyable =
        isFullCopy ||
        isTaskCopyable(task, filter.matchRule, categories, context);
      const copiedSteps = isExact
        ? getStepsInCategories(task, categories)
        : steps;
      const isChecked = ticked.has(task.taskId);
      // Why a locked task's tick can't change, on hover and for screen readers.
      let lockReason: string | null = null;
      if (context.lockedTaskIds.has(task.taskId)) {
        lockReason =
          steps.length === 0
            ? COPY_TOOL_TASK_NO_STEPS_REASON
            : COPY_TOOL_TASK_NO_CATEGORISED_STEPS_REASON;
      }
      // A full copy copies every task, so its rows show their steps rather than a reason.
      let reason: string | null = null;
      if (!isCopyable) reason = lockReason ?? COPY_TOOL_TASK_NO_MATCH_REASON;
      return {
        task,
        lockReason,
        isChecked,
        isCopied: isChecked && isCopyable,
        reason,
        stepNames: copiedSteps
          .map((step) => step.item)
          .filter(Boolean)
          .join(', '),
        stepCount:
          isExact && isCopyable && copiedSteps.length < steps.length
            ? COPY_TOOL_TASK_STEP_COUNT_LABEL(copiedSteps.length, steps.length)
            : null,
      };
    });
  }, [filter, context, isFullCopy]);

  const copiedCount = rows.filter((row) => row.isCopied).length;

  return (
    <fieldset className="flex w-full flex-col gap-1">
      <legend className="mb-1 text-sm font-semibold text-charcoal">
        {COPY_TOOL_TASKS_LABEL}
        {rows.length > 0 && (
          <>
            {' '}
            <span className="font-normal text-gray-muted">
              {COPY_TOOL_TASKS_COUNT_LABEL(copiedCount, rows.length)}
            </span>
          </>
        )}
      </legend>
      {rows.length === 0 && (
        <span className="text-xs text-gray-muted">
          {filter && context
            ? COPY_TOOL_TASKS_EMPTY_MESSAGE(modeLabel)
            : COPY_TOOL_TASKS_NO_SOURCE_MESSAGE}
        </span>
      )}
      {rows.length > 0 && (
        <ul className="max-h-[7.5rem] divide-y divide-gray-medium overflow-y-auto rounded-md border border-gray-medium">
          {rows.map(
            ({
              task,
              lockReason,
              isChecked,
              isCopied,
              reason,
              stepNames,
              stepCount,
            }) => (
              <Tooltip key={task.taskId} title={lockReason}>
                <li className="px-2 py-1">
                  <Checkbox
                    checked={isChecked}
                    disabled={lockReason !== null}
                    onChange={(event) =>
                      onTaskCheck(task.taskId, event.target.checked)
                    }
                    className="w-full"
                  >
                    <span
                      className={MergeClassNames(
                        'flex min-w-0 flex-col',
                        !isCopied && 'text-gray-muted'
                      )}
                    >
                      <span className="flex flex-wrap items-baseline gap-x-2 text-sm">
                        <span
                          className={MergeClassNames(
                            'font-semibold',
                            isCopied && 'text-charcoal'
                          )}
                        >
                          {getInputTaskTypeLabel(task.taskType)}
                        </span>
                        <span
                          className={MergeClassNames(
                            isCopied && 'text-gray-dark'
                          )}
                        >
                          {formatDate(task.endDate, task.endDateHasDay).trim()}
                        </span>
                        {stepCount && (
                          <span className="text-xs text-gray-muted">
                            {stepCount}
                          </span>
                        )}
                      </span>
                      {(reason ?? stepNames) && (
                        <span className="line-clamp-2 break-words text-xs text-gray-muted">
                          {reason ?? stepNames}
                        </span>
                      )}
                      {lockReason && !reason && (
                        <span className="sr-only">{lockReason}</span>
                      )}
                    </span>
                  </Checkbox>
                </li>
              </Tooltip>
            )
          )}
        </ul>
      )}
    </fieldset>
  );
};

export default CopyTaskChecklist;
